import { VehicleNumberPlate } from './vehicleNumberplates';
import { WalletType } from './wallet';

/**
 * Тип модального окна
 */
export enum ModalType {
	/** Модалка с текстом */
	Default = 'default',
	/** Модалка с текстом и ценой */
	Price = 'price',
	/** Модалка с выбором кол-ва и суммарной ценой */
	CountAndPrice = 'count_and_price',
	/** Модалка с информацией о тест-драйве авто */
	VehicleTestDrive = 'vehicle_test_drive',
	/** Модалка с информацией о приглашении */
	Invite = 'invite',
	/** Универсальная модалка для админских заявок (одобрить/отклонить с причиной) */
	AdminRequest = 'admin_request',
	/** Модалка снятия средств с бизнеса (касса/счёт) */
	BusinessWithdraw = 'business_withdraw',
	/** Модалка изменения цены товара бизнеса */
	BusinessChangePrice = 'business_change_price',
	/** Модалка заказа товара (со слайдером кол-ва и MAX) */
	BusinessOrder = 'business_order',
}

/**
 * Цвета для кнопок в модальном окне
 */
export enum ModalButtonColor {
	Success = 'success', // зеленый
	Warning = 'warning', // оранжевый
	Danger = 'danger', // красный
	Primary = 'primary', // синий
	Secondary = 'secondary', // серый
	Danger2 = 'danger2', // красный посветлее
}

/**
 * Поверхность, на которой будет отрисована модалка.
 * `Screen` — глобальный фуллскрин-лэйаут (по умолчанию).
 * `Tablet` — модалка рендерится внутри экрана планшета,
 *   затемнение ограничено пределами планшета.
 */
export enum ModalSurface {
	Screen = 'screen',
	Tablet = 'tablet',
}

/**
 * Маппинг типа модального окна на объект с данными
 */
type IModalStateByType = {
	[ModalType.Default]: IModalStateDefault;
	[ModalType.Price]: IModalStatePrice;
	[ModalType.CountAndPrice]: IModalStateCountAndPrice;
	[ModalType.VehicleTestDrive]: IModalStateVehicleTestDrive;
	[ModalType.Invite]: IModalStateInvite;
	[ModalType.AdminRequest]: IModalStateAdminRequest;
	[ModalType.BusinessWithdraw]: IModalStateBusinessWithdraw;
	[ModalType.BusinessChangePrice]: IModalStateBusinessChangePrice;
	[ModalType.BusinessOrder]: IModalStateBusinessOrder;
};

/**
 * Маппинг типа модального окна на объект с результатом, который вернет
 * модальное окно после нажатия любой кнопки.
 */
export type IModalReplyByType = {
	[ModalType.Default]: IModalReplyDefault;
	[ModalType.Price]: IModalReplyWithValue<WalletType | null>;
	[ModalType.CountAndPrice]: IModalReplyWithValue<number>;
	[ModalType.VehicleTestDrive]: IModalReplyDefault;
	[ModalType.Invite]: IModalReplyDefault;
	[ModalType.AdminRequest]: IModalReplyAdminRequest;
	[ModalType.BusinessWithdraw]: IModalReplyWithValue<number>;
	[ModalType.BusinessChangePrice]: IModalReplyWithValue<number>;
	[ModalType.BusinessOrder]: IModalReplyWithValue<number>;
};

/**
 * Параметры кнопки в модальном окне
 */
export interface IModalButtonOptions {
	text: string;
	color: ModalButtonColor;
}

/**
 * Доп. строка-поле в модалке.
 */
export interface IModalPriceField {
	/** Подпись слева */
	label: string;
	/** Основное значение */
	value: string;
	/** Приписка после значения */
	valueSuffix?: string;
	/** Цвет текста значения (по умолчанию #ffffff) */
	valueColor?: string;
	/** URL иконки справа */
	icon?: string;
	/** Цвет иконки (по умолчанию #ffbf42) */
	iconColor?: string;
}

// Типы модальных окон

/**
 * Обычное универсальное модальное окно с двумя кнопками
 */
export interface IModalStateDefault {
	type: ModalType.Default;
	title: string;
	text: string;
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	/** Поверхность отрисовки (по умолчанию ModalSurface.Screen) */
	surface?: ModalSurface;
}

/**
 * Модальное окно с текстом и ценой.
 */
export interface IModalStatePrice {
	type: ModalType.Price;
	title: string;
	text: string;
	/** Сумма, которая будет списана со счета */
	price: number;
	/** Сумма донат-валюты, которая будет списана со счета (в случае выбора способа оплаты через walletTypes) */
	priceDonate?: number;
	/** Тип кошелька, с которого будет списана сумма (влияет на иконку) */
	wallet?: WalletType;
	/** Типы кошельков, с которого будет списана сумма (если нужен выбор способа оплаты) */
	walletTypes?: WalletType[];
	/**
	 * Дополнительные поля, которые будут отрисованы между текстом и блоком «Стоимость».
	 */
	fields?: IModalPriceField[];
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	/** Поверхность отрисовки (по умолчанию ModalSurface.Screen) */
	surface?: ModalSurface;
}

/**
 * Модальное окно с выбором кол-ва и суммарной ценой (кол-во * цена)
 */
export interface IModalStateCountAndPrice {
	type: ModalType.CountAndPrice;
	title: string;
	text: string;
	/** Кол-во по умолчанию */
	count: number;
	/** Минимальное кол-во */
	minCount: number;
	/** Максимальное кол-во */
	maxCount: number;
	/** Цена за одну единицу (в модалке будет отображаться цена за сумму) */
	pricePerItem: number;
	/** Тип кошелька, с которого будет списана сумма (влияет на иконку) */
	wallet: WalletType;
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	/** Поверхность отрисовки (по умолчанию ModalSurface.Screen) */
	surface?: ModalSurface;
}

export interface IModalStateVehicleTestDrive {
	type: ModalType.VehicleTestDrive;
	/** Продолжительность тест-драйва */
	durationSec: number;
	price: number;
	wallet: WalletType;
	/** Название авто, которое берем на тест-драйв */
	vehicleName: string;
	/** Поверхность отрисовки (по умолчанию ModalSurface.Screen) */
	surface?: ModalSurface;
}

export interface IModalStateInvite {
	type: ModalType.Invite;
	title: string;
	inviteExpirationTime: number;
	/** Поверхность отрисовки. Invite всегда отображается фуллскрином. */
	surface?: ModalSurface;
}

/**
 * Модальное окно снятия средств с бизнеса.
 */
export interface IModalStateBusinessWithdraw {
	type: ModalType.BusinessWithdraw;
	title: string;
	text: string;
	/** Подпись к полю с балансом */
	balanceLabel: string;
	/** Доступный для снятия баланс */
	balance: number;
	/** Процент налога. Может быть дробным (например, 1.5) */
	taxPercent: number;
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	surface?: ModalSurface;
}

/**
 * Модальное окно изменения цены товара бизнеса.
 */
export interface IModalStateBusinessChangePrice {
	type: ModalType.BusinessChangePrice;
	title: string;
	text: string;
	/** Текущая (старая) цена за единицу */
	currentPrice: number;
	/** Максимальная допустимая цена за единицу */
	maxPrice: number;
	/** Минимальная допустимая цена за единицу (по умолчанию 1) */
	minPrice?: number;
	/** Единица измерения товара (например, «л.», «шт.», «кг»). Опционально */
	unit?: string;
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	surface?: ModalSurface;
}

/**
 * Модальное окно заказа товара для бизнеса:
 * слайдер + ввод количества, отображение текущего запаса,
 * максимума и итоговой стоимости.
 */
export interface IModalStateBusinessOrder {
	type: ModalType.BusinessOrder;
	title: string;
	text: string;
	/** Подпись к полю с текущим запасом */
	stockLabel: string;
	/** Текущее количество товара на складе */
	stock: number;
	/** Максимально возможное кол-во товара к заказу */
	maxCount: number;
	/** Минимально возможное кол-во товара к заказу (по умолчанию 1) */
	minCount?: number;
	/** Цена за единицу товара */
	pricePerItem: number;
	/** Единица измерения товара (например, «л.», «шт.»). Опционально */
	unit?: string;
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	surface?: ModalSurface;
}

/**
 * Строка в универсальной модалке админских заявок
 */
export type IAdminRequestModalRow =
	| {
			kind: 'text';
			label: string;
			value: string;
			/** Доп. приглушённый текст справа (например, "ID:109") */
			valueSubtext?: string;
	  }
	| {
			kind: 'numberplate';
			label: string;
			/** Подпись слева от номера (например, "Без флаговый") */
			secondaryLabel?: string;
			numberPlate: VehicleNumberPlate;
	  };

/**
 * Универсальная модалка админских заявок: список полей + опц. поле "причина" + 2 кнопки.
 * Используется под "Заявка на никнейм", "Заявка на номер" и в будущем для других заявок.
 */
export interface IModalStateAdminRequest {
	type: ModalType.AdminRequest;
	title: string;
	rows: IAdminRequestModalRow[];
	reasonPlaceholder?: string;
	acceptButton: IModalButtonOptions;
	cancelButton: IModalButtonOptions;
	surface?: ModalSurface;
}

// Ответы пользователя

export interface IModalReplyDefault {
	isAccepted: boolean;
	/**
	 * Закрытие крестиком в шапке: ни accept, ни кнопка отмены (см. ModalTitle).
	 * Если задано, то при закрытии модалки крестиком(или ESC) не будет отправлен ответ.
	 */
	dismissedByClose?: boolean;
}

export interface IModalReplyWithValue<T> extends IModalReplyDefault {
	value: T;
}

export interface IModalReplyAdminRequest extends IModalReplyDefault {
	reason: string;
}

export type IModalState = IModalStateByType[keyof IModalStateByType];
export type IModalReply<T extends IModalState> = IModalReplyByType[T['type']];
